/**
 * Turning a campaign into the messages it will actually send.
 *
 * Two callers want exactly the same answer: the send console, which asks for
 * it now, and the scheduler, which asks for it so the server can send it on
 * Friday at nine. Writing that out twice would mean two definitions of what
 * somebody receives, and the one that drifts is the one nobody is watching.
 *
 * Everything here is decided in the browser because everything it reads is
 * here: who an audience resolves to, what the client ledger says about them,
 * which addresses have opted out, and what a merge field fills in. The server
 * has no way to ask any of that, which is why a scheduled campaign is worked
 * out at the moment it is scheduled and stored whole.
 */
import { reachSummary, applyMerge, campaignRecipients } from './marketing.js';
import { looksHtml, htmlToText } from '../components/modals/CampaignEditor.jsx';

/**
 * Every message a campaign would send, personalised.
 *
 * `ok` is everyone reachable on the channel and not opted out; `notes` says
 * why anybody was left out, in the words the screens already use.
 */
export function outgoing(campaign, ctx, S) {
  const c = campaign || {};
  const { ok, notes } = reachSummary(campaignRecipients(c, ctx), c.channel, ctx);

  const footer = (S.mkt || {}).footer !== false ? '\n\nReply STOP to opt out.' : '';
  const source = String(c.body || '');

  /*
   * A campaign written in HTML still needs a plain-text twin: a mail client
   * that refuses markup shows the text part, and a message with no text part
   * at all is a spam signal in itself.
   */
  const flat = looksHtml(source) ? htmlToText(source) : source;
  const isMail = c.channel === 'email';
  const limit = c.channel === 'sms' ? ((S.mkt || {}).smsLimit || 160) : Infinity;

  /*
   * A WhatsApp campaign goes out as an approved template, and what travels
   * is not the text but the values for its blanks - one per {{n}}, filled in
   * for this person. The text is still built, for the console and the log,
   * but the provider never sees it.
   */
  const isWa = c.channel === 'whatsapp';
  const waFields = isWa ? (c.waFields || []) : null;

  const messages = ok.map(r => {
    const text = applyMerge(flat, r) + (isWa ? '' : footer);
    return {
      rid: r.id,
      name: r.name,
      to: isMail ? r.email : r.phone,
      subject: isMail ? applyMerge(c.subject || '', r) : undefined,
      text: isMail ? text : text.slice(0, limit),
      html: isMail && looksHtml(source) ? applyMerge(source, r) : undefined,
      fields: waFields ? waFields.map(f => applyMerge(String(f || ''), r).trim()) : undefined,
      // A template with a picture on top needs the picture's address.
      header: isWa && c.waHeaderUrl
        ? { type: (c.waHeader && c.waHeader.format) || 'image', url: c.waHeaderUrl }
        : undefined
    };
  });

  return { ok, notes, messages };
}

/**
 * How the campaign presents itself, from channel setup.
 *
 * Credentials stay on the server; the name on a campaign, the reply address
 * and the sender ID are content decisions, so they travel with the send.
 */
export function presentation(campaign, S) {
  const M = S.mkt || {};
  const c = campaign || {};
  return {
    accountId: c.accountId || undefined,
    fromName: M.fromName,
    fromEmail: M.fromEmail,
    replyTo: M.replyTo,
    sender: M.smsSender,
    // The campaign's own template first; channel setup is only the default.
    template: c.waTemplate || M.waTemplate,
    templateLang: c.waTemplateLang || M.waTemplateLang,
    templateVars: (c.waFields || []).length || M.waTemplateVars
  };
}
